import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Tooltip, Badge } from "@material-ui/core";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
  iconButtonBase: {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "50%",
    padding: 4,
    margin: 0,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    boxShadow:
      theme.mode === "light"
        ? "0 1px 2px rgba(16,24,40,0.06)"
        : "0 1px 2px rgba(0,0,0,0.30)",
  },
  iconStyle: {
    width: 20,
    height: 20,
    color: (props) => props.iconColor || theme.palette.action.active,
  },
  hoverState: {
    "& svg": {
      color: `${theme.palette.primary.main} !important`,
    },
    backgroundColor: `${theme.palette.action.hover} !important`,
  },
  selectedState: {
    "& svg": {
      color: `${theme.palette.primary.main} !important`,
    },
    backgroundColor: `${theme.palette.action.selected} !important`,
    "&:hover": {
      backgroundColor: `${theme.palette.action.hover} !important`,
    },
  },
  // ✅ Indicador de warning (ex: notificações bloqueadas)
  warningIndicator: {
    position: "absolute",
    top: -2,
    right: -2,
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: theme.palette.warning.main,
    border: `2px solid ${theme.palette.background.paper}`,
    animation: "$pulse 2s infinite",
  },
  "@keyframes pulse": {
    "0%": { opacity: 1 },
    "50%": { opacity: 0.5 },
    "100%": { opacity: 1 },
  },
  buttonWrapper: {
    position: "relative",
    display: "inline-flex",
  },
}));

const IconButtonWithTooltip = ({
  icon: IconComponent,
  tooltip,
  onClick,
  iconColor,
  badgeProps,
  className,
  isHovered,
  isSelected,
  showWarning, // ✅ Novo: indicador de warning
  "aria-label": ariaLabel,
  ...props
}) => {
  const classes = useStyles({ iconColor });

  // ♿ A11y: deriva aria-label do tooltip quando não for passado explicitamente.
  // Garante nome acessível em botões só-ícone sem quebrar a API (override manual prevalece).
  const a11yLabel =
    ariaLabel || (typeof tooltip === "string" ? tooltip : undefined);

  const rawIcon = typeof IconComponent === "function" ? IconComponent() : React.createElement(IconComponent);
  const iconElement = React.cloneElement(rawIcon, { className: clsx(rawIcon.props?.className, classes.iconStyle) });
  const buttonContent = badgeProps ? (
    <Badge {...badgeProps}>{iconElement}</Badge>
  ) : (
    iconElement
  );

  const buttonClassName = clsx(
    classes.iconButtonBase,
    isHovered && classes.hoverState,
    isSelected && classes.selectedState,
    className
  );

  return (
    <Tooltip title={tooltip || ""} arrow>
      <div className={classes.buttonWrapper}>
        <IconButton
          onClick={onClick}
          className={buttonClassName}
          size="small"
          aria-label={a11yLabel}
          {...props}
        >
          {buttonContent}
        </IconButton>
        {showWarning && <span className={classes.warningIndicator} />}
      </div>
    </Tooltip>
  );
};

export default IconButtonWithTooltip;
